import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAxios from "../useAxios";

const UserCompetitionView = ({ logout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [axios] = useAxios(true, logout);
  
  const [competition, setCompetition] = useState(null);
  const [knockout, setKnockout] = useState(null);
  const [userPlayer, setUserPlayer] = useState(null);
  const [isJoined, setIsJoined] = useState(false);

  // Get current user info to check if linked to player
  const getCurrentUser = () => {
    const token = sessionStorage.getItem("token");
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await axios.get("competition/" + id, setCompetition);
      await axios.get("competitionknockout/" + id, setKnockout);
      
      // Get current user and check if they're linked to a player
      const user = getCurrentUser();
      if (user && user.linked_player_id) {
        // Check if user is already joined to this competition
        // This would require an API endpoint to check participation
      }
    };
    
    fetchData();
  }, [id]);

  const handleJoinCompetition = async () => {
    // API call to join competition
    try {
      await axios.post(`competitions/${id}/join`, {});
      setIsJoined(true);
      // Refresh competition data
      await axios.get("competitionknockout/" + id, setKnockout);
    } catch (error) {
      console.error("Failed to join competition:", error);
      // Handle error (show message to user)
    }
  };

  const handleLeaveCompetition = async () => {
    // API call to leave competition
    try {
      await axios.post(`competitions/${id}/leave`, {});
      setIsJoined(false);
      // Refresh competition data
      await axios.get("competitionknockout/" + id, setKnockout);
    } catch (error) {
      console.error("Failed to leave competition:", error);
      // Handle error (show message to user)
    }
  };

  if (competition === null || knockout === null) {
    return <h1>Loading...</h1>;
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => {
    const statuses = {
      0: 'Pending',
      1: 'Started', 
      2: 'Finished'
    };
    return statuses[status] || 'Unknown';
  };

  const canJoin = competition.status === 0; // Only allow joining pending competitions
  const user = getCurrentUser();
  const hasLinkedPlayer = user && user.linked_player_id;

  return (
    <div className="user-competition-container">
      <div className="competition-header">
        <button onClick={() => navigate('/user/competitions')} className="btn-back">
          ← Back to Competitions
        </button>
        <h2>{competition.name}</h2>
      </div>

      <div className="competition-info-card">
        <div className="info-grid">
          <div className="info-item">
            <span className="label">Type:</span>
            <span className="value">{competition.type.name}</span>
          </div>
          
          <div className="info-item">
            <span className="label">Date:</span>
            <span className="value">{formatDate(competition.dateTime)}</span>
          </div>
          
          <div className="info-item">
            <span className="label">Status:</span>
            <span className="value status">{getStatusText(competition.status)}</span>
          </div>
          
          {knockout.raceTo && (
            <div className="info-item">
              <span className="label">Race To:</span>
              <span className="value">{knockout.raceTo}</span>
            </div>
          )}
          
          {competition.sameDay && (
            <div className="info-item">
              <span className="label">Format:</span>
              <span className="value">Same Day Competition</span>
            </div>
          )}
        </div>
      </div>

      <div className="players-section">
        <h3>Registered Players ({knockout.players ? knockout.players.length : 0})</h3>
        
        {knockout.players && knockout.players.length > 0 ? (
          <div className="players-list">
            {knockout.players.map((player, index) => (
              <div key={player.id} className="player-item">
                <span className="player-number">{index + 1}</span>
                <span className="player-name">{player.name}</span>
                <span className="player-handicap">({player.handicap})</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-players">No players registered yet.</p>
        )}
      </div>

      {hasLinkedPlayer && canJoin && (
        <div className="join-section">
          {!isJoined ? (
            <button 
              onClick={handleJoinCompetition}
              className="btn-join"
              disabled={competition.status !== 0}
            >
              Join Competition
            </button>
          ) : (
            <button 
              onClick={handleLeaveCompetition}
              className="btn-leave"
            >
              Leave Competition
            </button>
          )}
        </div>
      )}

      {!hasLinkedPlayer && (
        <div className="warning-section">
          <p>Your account is not linked to a player profile. Contact an administrator to link your account before joining competitions.</p>
        </div>
      )}

      {competition.status >= 1 && (
        <div className="draw-section">
          <button 
            onClick={() => navigate(`/user/competitions/${id}/draw`)}
            className="btn-view-draw"
          >
            {competition.status === 1 ? 'View Draw' : 'View Results'}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserCompetitionView;